import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { stripeService, PRICING_TIERS } from '@/services/stripe.service'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import { CreditCard, ExternalLink, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

export function SubscriptionStatus() {
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => stripeService.getSubscriptionStatus()
  })

  const { data: limits } = useQuery({
    queryKey: ['subscription-limits'],
    queryFn: () => stripeService.checkSubmissionLimit()
  })

  const handleManageBilling = async () => {
    setIsLoadingPortal(true)
    try {
      await stripeService.redirectToPortal()
    } catch (error) {
      toast.error('Failed to open billing portal')
      setIsLoadingPortal(false)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24" />
        </CardContent>
      </Card>
    )
  }

  const tier = PRICING_TIERS[subscription?.tier || 'trial']
  const statusColors: Record<string, string> = {
    active: 'bg-green-500',
    trialing: 'bg-blue-500',
    past_due: 'bg-yellow-500',
    canceled: 'bg-red-500',
    incomplete: 'bg-orange-500',
    unpaid: 'bg-red-500'
  }

  const statusLabels: Record<string, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past Due',
    canceled: 'Canceled',
    incomplete: 'Incomplete',
    unpaid: 'Unpaid'
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {tier.name} Plan
              <Badge
                className={statusColors[subscription?.status || 'trialing']}
              >
                {statusLabels[subscription?.status || 'trialing'] || 'Unknown'}
              </Badge>
            </CardTitle>
            <CardDescription>{tier.description}</CardDescription>
          </div>
          <CreditCard className="h-8 w-8 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Usage */}
        {limits && limits.limit > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span>Submissions Used</span>
              <span className="font-medium">
                {limits.current} / {limits.limit}
              </span>
            </div>
            <Progress
              value={(limits.current / limits.limit) * 100}
              className="h-2"
            />
            {!limits.allowed && (
              <p className="mt-2 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                You've reached your submission limit. Upgrade to continue.
              </p>
            )}
          </div>
        )}

        {/* Billing Period */}
        {subscription?.currentPeriodEnd && (
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">
              {subscription.cancelAtPeriodEnd ? (
                <>
                  Your subscription will end on{' '}
                  <span className="font-medium text-foreground">
                    {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}
                  </span>
                </>
              ) : (
                <>
                  Next billing date:{' '}
                  <span className="font-medium text-foreground">
                    {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}
                  </span>
                </>
              )}
            </p>
          </div>
        )}

        {/* Trial info */}
        {subscription?.status === 'trialing' && subscription.trialEnd && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Your free trial ends on{' '}
              <span className="font-medium">
                {format(new Date(subscription.trialEnd), 'MMMM d, yyyy')}
              </span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {subscription?.tier !== 'trial' && (
            <Button
              variant="outline"
              onClick={handleManageBilling}
              disabled={isLoadingPortal}
            >
              {isLoadingPortal ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Manage Billing
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
